// react_frontend/src/Components/Modules/profile.js
import React, { useRef, useState, useEffect, useContext } from "react";
import Breadcrumb from "../Partials/Breadcrumb";
import { AxiosAuthInstance } from "../../AxiosInterceptors";
import { MotherContext } from "../../Context/MotherContext";

// RSuite Components
import { Button, Input, Message, toaster, Panel, Divider, IconButton } from "rsuite";
import "rsuite/dist/rsuite.min.css";

// Icons
import { FaUser, FaEnvelope, FaPhone, FaKey, FaSave, FaRegEye, FaRegEyeSlash } from "react-icons/fa";

const Profile = () => {
  const { updateUser } = useContext(MotherContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [formLoading, setFormLoading] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const userRef = useRef();

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await AxiosAuthInstance.post("user_profile");
        setData(response.data.user || { name: "", email: "", phone: "" });
      } catch (error) {
        console.error(error);
        setData({ name: "", email: "", phone: "" });
        toaster.push(<Message type="error" duration={5000}>Failed to load profile data.</Message>, { placement: "topEnd" });
      } finally { setLoading(false); }
    };
    fetchProfile();
  }, []);

  const togglePasswordVisibility = () => setPasswordVisible(!passwordVisible);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setErrors({});

    const formData = {
      name: data.name?.trim(),
      email: data.email?.trim(),
      phone: data.phone?.trim(),
      password: userRef.current?.value?.trim() || undefined,
    };

    try {
      const response = await AxiosAuthInstance.post("user_profile_update", formData);

      if (response.data.status === true) {
        updateUser({ name: data.name });
        if (userRef.current) userRef.current.value = "";
        toaster.push(<Message type="success" duration={3000}>Profile updated successfully!</Message>, { placement: "topEnd" });
      }
    } catch (error) {
      if (error.response?.data?.errors) setErrors(error.response.data.errors);
      else toaster.push(<Message type="error" duration={5000}>Failed to update profile.</Message>, { placement: "topEnd" });
    } finally { setFormLoading(false); }
  };

  if (loading) return (
    <div className="p-3">
      <Panel header={<FaUser />} bordered shaded bodyFill>
        <div className="text-center py-5"><Message type="info">Loading profile...</Message></div>
      </Panel>
    </div>
  );

  return (
    <>
      <Breadcrumb title="Edit Profile" />
      <div className="p-3">
        <Panel header={<div className="d-flex align-items-center gap-2"><FaUser /> Edit Your Profile</div>} bordered shaded bodyFill>
          <form onSubmit={handleSubmit} className="p-3">
            <div className="text-center mb-3">
              <div className="rounded-circle mx-auto" style={{ width: "100px", height: "100px", backgroundColor: "#e9ecef", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "2rem", color: "#6c757d", border: "3px solid #dee2e6" }}>
                {data.name?.charAt(0)?.toUpperCase() || "U"}
              </div>
            </div>

            <Divider style={{ margin: "10px 0" }} />

            <div className="mb-3">
              <label className="d-flex align-items-center gap-2 text-dark mb-1"><FaUser /> Full Name</label>
              <Input value={data.name} onChange={val => setData({ ...data, name: val })} />
              {errors.name && <div className="text-danger mt-1"><small>{errors.name[0]}</small></div>}
            </div>

            <div className="mb-3">
              <label className="d-flex align-items-center gap-2 text-dark mb-1"><FaEnvelope /> Email Address</label>
              <Input type="email" value={data.email} onChange={val => setData({ ...data, email: val })} />
              {errors.email && <div className="text-danger mt-1"><small>{errors.email[0]}</small></div>}
            </div>

            <div className="mb-3">
              <label className="d-flex align-items-center gap-2 text-dark mb-1"><FaPhone /> Phone Number</label>
              <Input value={data.phone} onChange={val => setData({ ...data, phone: val })} />
              {errors.phone && <div className="text-danger mt-1"><small>{errors.phone[0]}</small></div>}
            </div>

            <div className="mb-3">
              <label className="d-flex align-items-center gap-2 text-dark mb-1"><FaKey /> New Password</label>
              <div className="d-flex align-items-center">
                <Input type={passwordVisible ? "text" : "password"} ref={userRef} placeholder="Leave blank to keep current" style={{ flexGrow: 1 }} />
                <IconButton icon={passwordVisible ? <FaRegEyeSlash /> : <FaRegEye />} onClick={togglePasswordVisibility} appearance="subtle" />
              </div>
              {errors.password && <div className="text-danger mt-1"><small>{errors.password[0]}</small></div>}
            </div>

            <Divider style={{ margin: "20px 0" }} />
            <div className="d-flex justify-content-end">
              <Button type="submit" appearance="primary" loading={formLoading}><FaSave /> Save</Button>
            </div>
          </form>
        </Panel>
      </div>
    </>
  );
};

export default Profile;
